import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { BackgroundColorExtension } from './background-color-extension';
import { Button } from '@/components/ui/button';
import { 
  Bold, 
  Italic, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  List,
  ListOrdered,
  Undo,
  Redo,
  Type,
  Highlighter,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  availablePlaceholders?: string[]; // Array of available placeholders
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  className,
  availablePlaceholders = []
}) => {
  const [isHighlightActive, setIsHighlightActive] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      BackgroundColorExtension,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'my-highlight',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onSelectionUpdate: ({ editor }) => {
      // Helper function to check if color matches our highlight color
      const isHighlightColor = (color: string | null | undefined): boolean => {
        if (!color) return false;
        // Normalize color formats - convert rgb(248, 202, 198) to #f8cac6
        if (color.startsWith('rgb(')) {
          const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            const [, r, g, b] = rgbMatch;
            const hex = '#' + [r, g, b].map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
            return hex === '#f8cac6';
          }
        }
        return color === '#f8cac6';
      };
      
      // Check if current selection has highlight using multiple approaches
      const { from, to } = editor.state.selection;
      let hasHighlight = false;
      
      // Approach 1: Check current attributes
      const currentAttributes = editor.getAttributes('textStyle');
      if (isHighlightColor(currentAttributes.backgroundColor)) {
        hasHighlight = true;
      }
      
      // Approach 2: Check all marks in selection
      if (!hasHighlight) {
        editor.state.doc.nodesBetween(from, to, (node) => {
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type.name === 'textStyle' && isHighlightColor(mark.attrs.backgroundColor)) {
                hasHighlight = true;
              }
            });
          }
        });
      }
      
      // Approach 3: Check if the selection contains any span with the background color
      if (!hasHighlight && from !== to) {
        const selectedHtml = editor.getHTML().slice(0); // Get current HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = selectedHtml;
        const highlightedSpans = tempDiv.querySelectorAll('span[style*="background-color"]');
        highlightedSpans.forEach(span => {
          const style = (span as HTMLElement).style.backgroundColor;
          if (isHighlightColor(style)) {
            // Check if this span is within our selection range
            hasHighlight = true;
          }
        });
      }
      
      setIsHighlightActive(hasHighlight);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3 [&_.ProseMirror]:whitespace-pre-wrap',
      },
    },
    parseOptions: {
      preserveWhitespace: 'full',
    },
  });

  // Keep TipTap in sync when content is replaced externally (e.g. consent type switch)
  useEffect(() => {
    if (!editor) return;
    if (content === editor.getHTML()) return;
    editor.commands.setContent(content, { emitUpdate: false });
  }, [editor, content]);

  // Initialize highlight state when editor is ready
  useEffect(() => {
    if (editor) {
      const { from, to } = editor.state.selection;
      const currentAttributes = editor.getAttributes('textStyle');
      
      // Helper function to check if color matches our highlight color
      const isHighlightColor = (color: string | null | undefined): boolean => {
        if (!color) return false;
        // Normalize color formats - convert rgb(248, 202, 198) to #f8cac6
        if (color.startsWith('rgb(')) {
          const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            const [, r, g, b] = rgbMatch;
            const hex = '#' + [r, g, b].map(x => parseInt(x).toString(16).padStart(2, '0')).join('');
            return hex === '#f8cac6';
          }
        }
        return color === '#f8cac6';
      };
      
      let hasHighlight = isHighlightColor(currentAttributes.backgroundColor);
      
      if (!hasHighlight) {
        editor.state.doc.nodesBetween(from, to, (node) => {
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type.name === 'textStyle' && isHighlightColor(mark.attrs.backgroundColor)) {
                hasHighlight = true;
              }
            });
          }
        });
      }
      
      setIsHighlightActive(hasHighlight);
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({ 
    onClick, 
    isActive = false, 
    disabled = false, 
    children, 
    title 
  }: {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }) => (
    <Button
      type="button"
      variant={isActive ? "default" : "ghost"}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="h-8 w-8 p-0"
    >
      {children}
    </Button>
  );

  return (
    <div className={cn("border rounded-md", className)}>
      {/* Toolbar */}
      <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            if (isHighlightActive) {
              // Try multiple approaches to remove highlighting
              editor.chain().focus().unsetBackgroundColor().run();
              // Also try removing the textStyle mark entirely if it has no other attributes
              const attrs = editor.getAttributes('textStyle');
              const hasOtherAttrs = Object.keys(attrs).some(key => key !== 'backgroundColor' && attrs[key]);
              if (!hasOtherAttrs) {
                editor.chain().focus().unsetMark('textStyle').run();
              }
            } else {
              editor.chain().focus().setBackgroundColor('#f8cac6').run();
            }
          }}
          isActive={isHighlightActive}
          title="Highlight Background"
        >
          <Highlighter className="h-4 w-4" />
        </ToolbarButton>

        {/* Placeholder Insertion */}
        {availablePlaceholders.length > 0 && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-1" />
            <div className="relative">
              <select
                className="appearance-none bg-transparent border border-gray-300 rounded px-4 py-1 text-sm cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => {
                  const placeholder = e.target.value;
                  if (placeholder) {
                    editor.chain().focus().insertContent(placeholder).run();
                    e.target.value = ''; // Reset selection
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>Insert Placeholder</option>
                {availablePlaceholders.map((placeholder) => (
                  <option key={placeholder} value={placeholder}>
                    {placeholder}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none" />
            </div>
          </>
        )}

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Text Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Type className="h-4 w-4" />
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="min-h-[200px] [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:prose [&_.ProseMirror]:max-w-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror_p]:mt-0 [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_ul]:my-4 [&_.ProseMirror_ol]:my-4 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-6 [&_.ProseMirror_li]:list-item"
      />
    </div>
  );
};